import { cn } from '../../lib/utils'
import { Skeleton } from '../ui/skeleton'

export type CalmLoadingContext = 'auth' | 'profile' | 'tenant' | 'default'

const CONTEXT_MESSAGES: Record<CalmLoadingContext, string> = {
  auth: 'Securing your session…',
  profile: 'Loading your profile…',
  tenant: 'Syncing your workspace…',
  default: 'Preparing your workspace…',
}

type CalmLoadingShellProps = {
  /** Full viewport shell, compact card-style, or a single inline row */
  variant?: 'fullscreen' | 'compact' | 'inline'
  /** Override microcopy */
  message?: string
  /** Preset copy for common boot steps */
  context?: CalmLoadingContext
  className?: string
}

/**
 * Branded loading UI: soft skeletons plus calm microcopy (Calm Control).
 * Prefer over raw spinners and generic "Loading…" strings.
 */
export function CalmLoadingShell({
  variant = 'fullscreen',
  message,
  context = 'default',
  className,
}: CalmLoadingShellProps) {
  const text = message ?? CONTEXT_MESSAGES[context]

  if (variant === 'inline') {
    return (
      <div className={cn('flex items-center gap-2 min-w-0', className)} aria-busy="true" aria-live="polite">
        <Skeleton className="h-4 w-4 shrink-0 rounded-full" />
        <span className="text-sm text-muted-foreground truncate">{text}</span>
      </div>
    )
  }

  if (variant === 'compact') {
    return (
      <div
        className={cn('flex flex-col items-center gap-4 py-4', className)}
        aria-busy="true"
        aria-live="polite"
      >
        <div className="flex gap-2 w-full max-w-[220px]">
          <Skeleton className="h-2 flex-1 rounded-full bg-[#E2E8F0]" />
          <Skeleton className="h-2 w-14 rounded-full bg-[#E2E8F0]" />
        </div>
        <p className="text-sm font-medium text-muted-foreground text-center tracking-tight">{text}</p>
      </div>
    )
  }

  return (
    <div
      className={cn('min-h-screen bg-background flex items-center justify-center px-6', className)}
      aria-busy="true"
      aria-live="polite"
    >
      <div className="w-full max-w-[320px] flex flex-col items-center gap-8">
        <div className="w-full space-y-3">
          <Skeleton className="h-2.5 w-[72%] mx-auto rounded-full bg-[#E2E8F0]" />
          <Skeleton className="h-2.5 w-full rounded-full bg-[#E2E8F0]" />
          <Skeleton className="h-2.5 w-[88%] mx-auto rounded-full bg-[#E2E8F0]" />
        </div>
        <p className="text-sm font-medium text-muted-foreground text-center tracking-tight">{text}</p>
      </div>
    </div>
  )
}

/** Minimal route-chunk fallback: matches admin shell height without a second full layout paint. */
export function AppRouteSuspenseFallback({ className }: { className?: string }) {
  return (
    <div
      className={cn('flex h-[min(100dvh,100vh)] w-full items-center justify-center bg-background px-6', className)}
      aria-busy="true"
      aria-live="polite"
    >
      <div className="w-full max-w-[280px] flex flex-col items-center gap-6">
        <div className="w-full space-y-2.5">
          <Skeleton className="h-2 w-[80%] mx-auto rounded-full bg-[#E2E8F0]" />
          <Skeleton className="h-2 w-full rounded-full bg-[#E2E8F0]" />
          <Skeleton className="h-2 w-[75%] mx-auto rounded-full bg-[#E2E8F0]" />
        </div>
        <p className="text-sm font-medium text-muted-foreground text-center">Preparing this screen…</p>
      </div>
    </div>
  )
}
